import { getWorkDate } from "@components/inputs/date/utils";
import { BulkChangeAction } from "@components/smart/smartBulkChangeDialog/SmartBulkChange.utils";
import { getDefaultPercetageFromImpactCode } from "@pages/cashBasisAccounting/CashBasisAccounting.utils";
import { TFieldDefinition } from "@pages/PageUtils";

import { FieldType } from "../../enums";
import { ITableStorageDefaultCustomData } from "../../model/TableStorage";
import BindingContext, { IEntity } from "../../odata/BindingContext";
import { CbaCategoryTaxImpactCode, DocumentTypeCode, SelectionCode } from "../../odata/GeneratedEnums";
import { getUtcDayjs } from "../../types/Date";

export interface IBulkChangeContext {
    documentTypeCode: DocumentTypeCode;
    isTaxProformaDocument: boolean;
}

export interface IProformaTableCustomData extends ITableStorageDefaultCustomData {
    isTaxProforma?: boolean;
}

export enum BulkChangeDateSource {
    Today = "Today",
    DateIssued = "DateIssued",
    DateReceived = "DateReceived",
    DateTaxableSupply = "DateTaxableSupply",
    Own = "Own"
}

export type TBulkChangeDateSourceChangeType = "DateVatDeduction" | "DateAccountingTransaction";

export const BULK_CHANGE_DATE_SOURCE_OWN_DATE_PATHS: Record<TBulkChangeDateSourceChangeType, string> = {
    DateVatDeduction: BindingContext.localContext("DateVatDeductionOwnDate"),
    DateAccountingTransaction: BindingContext.localContext("DateAccountingTransactionOwnDate")
};

type ProformaDocumentTypes = DocumentTypeCode.ProformaInvoiceReceived | DocumentTypeCode.ProformaInvoiceIssued;

type ProformaDocumentTypeCondition = {
    documentTypeCode: ProformaDocumentTypes;
    isTaxProformaDocument?: boolean;
};

type RegularDocumentTypeCondition = {
    documentTypeCode: Exclude<DocumentTypeCode, ProformaDocumentTypes>;
    isTaxProformaDocument?: never;
};

export type TBulkChangeDocumentTypeCondition = ProformaDocumentTypeCondition | RegularDocumentTypeCondition;

type TBulkChangeAllowedDocumentTypes = TBulkChangeDocumentTypeCondition[] | "All";

export interface IBulkChangeFieldDef {
    changeType: string;
    fieldType: FieldType;
    allowedDocumentTypes: TBulkChangeAllowedDocumentTypes;
    defaultValue?: TFieldDefinition["defaultValue"];

    group: IBulkChangeGroup;
    /** Key for the value in the bulk change payload. Defaults to "Value" if not set. */
    valueAlias?: string;
    /** ChangeType sent to the API. Defaults to changeType if not set. */
    payloadChangeType?: string;
    /** Path in bulkData for reading the raw value. Defaults to changeType if not set. */
    dataPath?: string;
    /** Optional value transform applied before sending to the API. */
    getPayloadValue?: (rawValue: unknown, bulkData: IEntity) => unknown;
    /** Optional extra payload fields read from the whole bulk form data. */
    getAdditionalPayload?: (bulkData: IEntity) => Record<string, unknown>;
    /** If true, the value key is omitted for Remove/RemoveAll actions. */
    omitValueOnRemove?: boolean;
}

export interface IBulkChangeGroup {
    id: string;
    translationKey: string;
}

const BULK_CHANGE_GROUP = {
    Note: { id: "Note", translationKey: "Document:FormGroup.Note" },
    Document: { id: "document", translationKey: "Document:FormGroup.Document" },
    Date: { id: "date", translationKey: "Document:FormGroup.Date" },
    WithoutVat: { id: "Assignment", translationKey: "Document:FormGroup.WithoutVat" },
    Vat: { id: "Vats", translationKey: "Document:FormGroup.Vat" },
    CashBasisAccounting: { id: "CashBasisAccounting", translationKey: "Document:FormGroup.CashBasisAccounting" }
};

const isRemoveAction = (action: BulkChangeAction): boolean =>
    action === BulkChangeAction.Remove || action === BulkChangeAction.RemoveAll;

const documentTypes = (documentTypeCodes: DocumentTypeCode[]): TBulkChangeDocumentTypeCondition[] =>
    documentTypeCodes.map(documentTypeCode => ({ documentTypeCode } as TBulkChangeDocumentTypeCondition));

const isProformaDocumentType = (documentTypeCode: DocumentTypeCode): documentTypeCode is ProformaDocumentTypes =>
    documentTypeCode === DocumentTypeCode.ProformaInvoiceReceived || documentTypeCode === DocumentTypeCode.ProformaInvoiceIssued;

const proformaDocumentType = (
    documentTypeCode: ProformaDocumentTypes,
    isTaxProformaDocument?: boolean
): TBulkChangeDocumentTypeCondition => ({
    documentTypeCode,
    ...(isTaxProformaDocument !== undefined ? { isTaxProformaDocument } : {})
});

const isDocumentTypeAllowed = (
    condition: TBulkChangeDocumentTypeCondition,
    context: IBulkChangeContext
): boolean => {
    if (condition.documentTypeCode !== context.documentTypeCode) {
        return false;
    }

    if (isProformaDocumentType(condition.documentTypeCode) && condition.isTaxProformaDocument !== undefined) {
        return condition.isTaxProformaDocument === context.isTaxProformaDocument;
    }

    return true;
};

const ALL_BULK_CHANGE_DOCUMENT_TYPES_EXCEPT_PROFORMA = documentTypes([
    DocumentTypeCode.InvoiceReceived,
    DocumentTypeCode.InvoiceIssued,
    DocumentTypeCode.OtherLiability,
    DocumentTypeCode.OtherReceivable,
    DocumentTypeCode.CorrectiveInvoiceReceived,
    DocumentTypeCode.CorrectiveInvoiceIssued,
    DocumentTypeCode.InternalDocument
]);

const ACCOUNTING_BULK_CHANGE_DOCUMENT_TYPES = [
    ...ALL_BULK_CHANGE_DOCUMENT_TYPES_EXCEPT_PROFORMA,
    proformaDocumentType(DocumentTypeCode.ProformaInvoiceReceived, true),
    proformaDocumentType(DocumentTypeCode.ProformaInvoiceIssued, true)
];

const NOTE_FIELD_DEF: IBulkChangeFieldDef = {
    changeType: "Note",
    fieldType: FieldType.EditableText,
    allowedDocumentTypes: "All",
    group: BULK_CHANGE_GROUP.Note
};

const LABELS_FIELD_DEF: IBulkChangeFieldDef = {
    changeType: "Labels",
    fieldType: FieldType.LabelSelect,
    allowedDocumentTypes: "All",
    group: BULK_CHANGE_GROUP.Document,
    getPayloadValue: (rawValue) => (rawValue as IEntity[])?.map(item => ({
        LabelHierarchyId: (item.LabelHierarchy as IEntity)?.Id,
        LabelId: (item.Label as IEntity)?.Id,
        Name: item.Label?.Name
    }))
};

const EXPLANATION_FIELD_DEF: IBulkChangeFieldDef = {
    changeType: "Explanation",
    fieldType: FieldType.EditableText,
    allowedDocumentTypes: documentTypes([DocumentTypeCode.InternalDocument]),
    group: BULK_CHANGE_GROUP.Document
};

const getDateFieldDef = (changeType: string, allowedDocumentTypes: IBulkChangeFieldDef["allowedDocumentTypes"] = "All"): IBulkChangeFieldDef => ({
    changeType,
    fieldType: FieldType.Date,
    allowedDocumentTypes,
    defaultValue: getWorkDate,
    valueAlias: changeType,
    group: BULK_CHANGE_GROUP.Date,
    getPayloadValue: (rawValue) => rawValue ? getUtcDayjs(rawValue as Date).format("YYYY-MM-DD") : null
});

const getBulkDateValue = (bulkData: IEntity, path: string): string | null => {
    const rawValue = bulkData[path];
    return rawValue ? getUtcDayjs(rawValue as Date).format("YYYY-MM-DD") : null;
};

const getDateSourceFieldDef = (
    changeType: TBulkChangeDateSourceChangeType,
    allowedDocumentTypes: IBulkChangeFieldDef["allowedDocumentTypes"]
): IBulkChangeFieldDef => ({
    changeType,
    fieldType: FieldType.ComboBox,
    allowedDocumentTypes,
    group: BULK_CHANGE_GROUP.Date,
    valueAlias: changeType,
    omitValueOnRemove: true,
    getPayloadValue: (rawValue, bulkData) => ({
        Source: rawValue,
        ...(rawValue === BulkChangeDateSource.Own ? {
            Date: getBulkDateValue(bulkData, BULK_CHANGE_DATE_SOURCE_OWN_DATE_PATHS[changeType])
        } : {})
    })
});

export const BULK_CHANGE_DATE_ISSUED_DOCUMENT_TYPES = [
    DocumentTypeCode.InvoiceReceived,
    DocumentTypeCode.InvoiceIssued,
    DocumentTypeCode.OtherLiability,
    DocumentTypeCode.OtherReceivable,
    DocumentTypeCode.ProformaInvoiceReceived,
    DocumentTypeCode.ProformaInvoiceIssued,
    DocumentTypeCode.CorrectiveInvoiceReceived,
    DocumentTypeCode.CorrectiveInvoiceIssued
];

export const BULK_CHANGE_DATE_TAXABLE_SUPPLY_DOCUMENT_TYPES = [
    DocumentTypeCode.InvoiceReceived,
    DocumentTypeCode.InvoiceIssued,
    DocumentTypeCode.OtherLiability,
    DocumentTypeCode.OtherReceivable,
    DocumentTypeCode.ProformaInvoiceReceived,
    DocumentTypeCode.ProformaInvoiceIssued,
    DocumentTypeCode.CorrectiveInvoiceReceived,
    DocumentTypeCode.CorrectiveInvoiceIssued
];

export const BULK_CHANGE_DATE_DUE_DOCUMENT_TYPES = [
    DocumentTypeCode.InvoiceReceived,
    DocumentTypeCode.InvoiceIssued,
    DocumentTypeCode.OtherLiability,
    DocumentTypeCode.OtherReceivable,
    DocumentTypeCode.ProformaInvoiceReceived,
    DocumentTypeCode.ProformaInvoiceIssued,
    DocumentTypeCode.CorrectiveInvoiceReceived,
    DocumentTypeCode.CorrectiveInvoiceIssued
];

export const BULK_CHANGE_DATE_VAT_DEDUCTION_DOCUMENT_TYPES = [
    DocumentTypeCode.InvoiceReceived,
    DocumentTypeCode.OtherLiability,
    DocumentTypeCode.ProformaInvoiceReceived,
    DocumentTypeCode.CorrectiveInvoiceReceived
];

export const BULK_CHANGE_DATE_RECEIVED_DOCUMENT_TYPES = [
    DocumentTypeCode.InvoiceReceived,
    DocumentTypeCode.OtherLiability,
    DocumentTypeCode.ProformaInvoiceReceived,
    DocumentTypeCode.CorrectiveInvoiceReceived
];

export const BULK_CHANGE_DATE_PAYMENT_RECEIVED_DOCUMENT_TYPES = [
    DocumentTypeCode.ProformaInvoiceIssued
];

const DATE_FIELD_DEFS: IBulkChangeFieldDef[] = [
    getDateFieldDef("DateIssued", documentTypes(BULK_CHANGE_DATE_ISSUED_DOCUMENT_TYPES)),
    getDateFieldDef("DateTaxableSupply", documentTypes(BULK_CHANGE_DATE_TAXABLE_SUPPLY_DOCUMENT_TYPES)),
    getDateFieldDef("DateDue", documentTypes(BULK_CHANGE_DATE_DUE_DOCUMENT_TYPES)),
    getDateSourceFieldDef(
        "DateVatDeduction",
        [
            ...documentTypes([
                DocumentTypeCode.InvoiceReceived,
                DocumentTypeCode.OtherLiability,
                DocumentTypeCode.CorrectiveInvoiceReceived
            ]),
            proformaDocumentType(DocumentTypeCode.ProformaInvoiceReceived, true)
        ]
    ),
    getDateFieldDef("DateReceived", documentTypes(BULK_CHANGE_DATE_RECEIVED_DOCUMENT_TYPES)),
    getDateFieldDef("DatePaymentReceived", [
        proformaDocumentType(DocumentTypeCode.ProformaInvoiceIssued, true)
    ])
];

const VAT_CLASSIFICATION_SELECTION_FIELD_DEF: IBulkChangeFieldDef = {
    changeType: "VatClassificationSelection/VatClassification",
    fieldType: FieldType.ComboBox,
    allowedDocumentTypes: [
        ...documentTypes([
            DocumentTypeCode.InvoiceReceived,
            DocumentTypeCode.InvoiceIssued,
            DocumentTypeCode.OtherLiability,
            DocumentTypeCode.OtherReceivable,
            DocumentTypeCode.CorrectiveInvoiceReceived,
            DocumentTypeCode.CorrectiveInvoiceIssued
        ]),
        proformaDocumentType(DocumentTypeCode.ProformaInvoiceReceived, true),
        proformaDocumentType(DocumentTypeCode.ProformaInvoiceIssued, true)
    ],
    payloadChangeType: "VatClassificationSelection",
    dataPath: "VatClassificationSelection",
    group: BULK_CHANGE_GROUP.Vat,
    omitValueOnRemove: true,
    getPayloadValue: (rawValue: unknown): unknown => {
        const selection = rawValue as IEntity;
        const vatClassificationId = (selection?.VatClassification as IEntity)?.Id;
        const hasClassification = typeof vatClassificationId === "number";
        return {
            SelectionCode: hasClassification ? SelectionCode.Copy : SelectionCode.None,
            VatClassificationId: hasClassification ? vatClassificationId : null,
            Name: selection?.VatClassification?.Name
        };
    }
};

const CBA_CATEGORY_FIELD_DEF: IBulkChangeFieldDef = {
    changeType: "CbaCategory/Category",
    fieldType: FieldType.ComboBox,
    allowedDocumentTypes: "All",
    payloadChangeType: "CbaCategory",
    dataPath: "CbaCategory",
    valueAlias: "CbaCategoryId",
    group: BULK_CHANGE_GROUP.CashBasisAccounting,
    getPayloadValue: (rawValue) => (rawValue as IEntity)?.Category?.Id ?? null,
    omitValueOnRemove: true
};

const CBA_TAX_IMPACT_FIELD_DEF: IBulkChangeFieldDef = {
    changeType: "CbaCategory/TaxImpact",
    fieldType: FieldType.SegmentedButton,
    allowedDocumentTypes: "All",
    payloadChangeType: "TaxImpact",
    dataPath: "CbaCategory",
    group: BULK_CHANGE_GROUP.CashBasisAccounting,
    getPayloadValue: (rawValue): unknown => {
        const cbaCategory = rawValue as IEntity;
        const taxImpactCode = ((cbaCategory?.TaxImpact as IEntity)?.Code ?? cbaCategory?.TaxImpactCode) as CbaCategoryTaxImpactCode;

        return {
            TaxImpactCode: taxImpactCode,
            TaxPercentage: taxImpactCode === CbaCategoryTaxImpactCode.Partial ? cbaCategory?.TaxPercentage ?? 0 : getDefaultPercetageFromImpactCode(taxImpactCode),
            IsAssetAcquisition: taxImpactCode === CbaCategoryTaxImpactCode.Nontax ? !!cbaCategory?.IsAssetAcquisition : false
        };
    }
};

export const ACCOUNTING_BULK_CHANGE_FIELD_DEFS: IBulkChangeFieldDef[] = [
    NOTE_FIELD_DEF,
    EXPLANATION_FIELD_DEF,
    LABELS_FIELD_DEF,
    {
        changeType: "DefaultGrantAssignment/Grant",
        fieldType: FieldType.ComboBox,
        allowedDocumentTypes: "All",
        payloadChangeType: "DefaultGrantAssignment",
        dataPath: "DefaultGrantAssignment",
        group: BULK_CHANGE_GROUP.Document,
        getPayloadValue: (rawValue) => {
            return {
                GrantId: (rawValue as IEntity)?.Grant?.Id ?? null,
                Name: (rawValue as IEntity)?.Grant?.ShortName
            };
        },
        omitValueOnRemove: true
    },
    ...DATE_FIELD_DEFS,
    getDateSourceFieldDef(
        "DateAccountingTransaction",
        ACCOUNTING_BULK_CHANGE_DOCUMENT_TYPES
    ),
    {
        changeType: "AccountAssignmentSelection/AccountAssignment",
        fieldType: FieldType.ComboBox,
        allowedDocumentTypes: ACCOUNTING_BULK_CHANGE_DOCUMENT_TYPES,
        payloadChangeType: "AccountAssignmentSelection",
        dataPath: "AccountAssignmentSelection",
        group: BULK_CHANGE_GROUP.WithoutVat,
        omitValueOnRemove: true,
        getPayloadValue: (rawValue: unknown): unknown => {
            const selection = rawValue as IEntity;
            const assignment = selection?.AccountAssignment as IEntity;
            const selectionCode = (selection?.Selection as IEntity)?.Code as SelectionCode;
            return {
                SelectionCode: selectionCode,
                AccountAssignment: selectionCode === SelectionCode.None ? null : {
                    CreditAccountNumber: (assignment?.CreditAccount as IEntity)?.Number ?? null,
                    DebitAccountNumber: (assignment?.DebitAccount as IEntity)?.Number ?? null,
                    AccountAssignmentName: assignment?.Name ?? null,
                }
            };
        }
    },
    VAT_CLASSIFICATION_SELECTION_FIELD_DEF
];

export const CBA_BULK_CHANGE_FIELD_DEFS: IBulkChangeFieldDef[] = [
    NOTE_FIELD_DEF,
    EXPLANATION_FIELD_DEF,
    LABELS_FIELD_DEF,
    CBA_CATEGORY_FIELD_DEF,
    CBA_TAX_IMPACT_FIELD_DEF,
    getDateFieldDef("DateCbaEntry", documentTypes([DocumentTypeCode.InternalDocument])),
    ...DATE_FIELD_DEFS,
    VAT_CLASSIFICATION_SELECTION_FIELD_DEF
];

export const getBulkChangeDefsForDocumentType = (defs: IBulkChangeFieldDef[], context: IBulkChangeContext): IBulkChangeFieldDef[] => {
    return defs.filter(def =>
        def.allowedDocumentTypes === "All" || def.allowedDocumentTypes.some(condition => isDocumentTypeAllowed(condition, context))
    );
};

export const buildBulkChangePayloadEntry = (
    def: IBulkChangeFieldDef,
    action: BulkChangeAction,
    bulkData: IEntity
): Record<string, unknown> => {
    const payloadChangeType = def.payloadChangeType ?? def.changeType;
    const valueAlias = def.valueAlias ?? "Value";
    const rawValue = bulkData[def.dataPath ?? def.changeType];
    const remove = isRemoveAction(action);
    const value = def.getPayloadValue ? def.getPayloadValue(rawValue, bulkData) : rawValue;
    const additionalPayload = !remove && def.getAdditionalPayload ? def.getAdditionalPayload(bulkData) : null;

    return {
        ChangeType: payloadChangeType,
        Kind: action,
        ...(def.omitValueOnRemove && remove ? {} : { [valueAlias]: value }),
        ...additionalPayload
    };
};
